import { Alert, Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import useRequest from "@entrilia/shared/hooks/useRequest";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import adminApi from "../../../api/adminApi";
import type { Dashboard } from "../../../api/types/dashboardsTypes.ts";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";

const DeleteDashboardsDialog = ({
  open,
  dashboards,
  onClose,
  onDeleted,
}: {
  open: boolean;
  dashboards: Dashboard[];
  onClose: () => void;
  onDeleted: () => void;
}) => {
  const deleteDashboards = useCallback(() => {
    return adminApi.deleteDashboards(dashboards.map((dashboard) => dashboard.dashboardId));
  }, [dashboards]);

  const { request, error, isLoading: isDeleting } = useRequest(deleteDashboards);

  const handleDelete = useCallback(async () => {
    const [, error] = await request();
    if (!error) {
      onClose();
      onDeleted();
    }
  }, [request, onClose, onDeleted]);

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Delete {pluralize("Dashboard", dashboards.length)}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center", textAlign: "center" }}>
          {error && (
            <Alert severity="error" sx={{ width: "100%" }}>
              Failed to delete dashboards
            </Alert>
          )}
          <DeleteDialogIcon sx={{ fontSize: 80, color: "white" }} />

          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            {dashboards.length > 1 ? "Delete selected dashboards" : `Delete ${dashboards[0]?.nameUserInput} dashboard`}?
          </Typography>

          <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: "normal" }}>
            You&apos;re about to delete {dashboards.length > 1 ? "these dashboards" : "this dashboard"}. This action is
            permanent and can&apos;t be undone.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={isDeleting} disabled={isDeleting} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteDashboardsDialog;
